import { CSSProperties } from 'react'
import palette from 'theme/palette'

import { alpha } from '@mui/material/styles'

import { TEXT_ROW_LINE_HEIGHT, TEXT_ROW_MIN_LINES } from './constants'

// Matches the sibling ผลเชื้อ / ข้อมูลบ่อ tables: the mobile WebView renders the
// table at 26px and the zoom wrapper (initialScale 0.5) scales it down to a legible
// ~13px. Every size below is in em off this base, so the desktop table and the
// WebView resolve to the same proportions from one set of numbers.
export const MOBILE_FONT_SIZE = '1.625rem'
export const DESKTOP_FONT_SIZE = '0.875rem'

// NK-281 layout (Pond Size Compact): a pinned รายการ column and fixed-width date
// columns, one block per pond.
const LABEL_COL_WIDTH = '9em'
const DATE_COL_WIDTH = '6.6em'
const HEADER_HEIGHT = '3em'
const ROW_HEIGHT = '3.5em'

const { grey, primary } = palette.light

// A 1px rule is scaled to half a pixel by the WebView's 0.5 zoom and vanishes.
const hairline = (isMobile: boolean, color: string) => `${isMobile ? 2 : 1}px solid ${color}`

export const HIGHLIGHT_BACKGROUND = alpha(primary.main, 0.1)

// The floor for a wrapping row: three lines of text plus the cell's own padding,
// in em so it is the same three lines on desktop and in the WebView.
const TEXT_ROW_MIN_HEIGHT = `calc(${TEXT_ROW_MIN_LINES * TEXT_ROW_LINE_HEIGHT}em + 0.5em)`

export const tableStyles = {
  // On desktop the page scrolls, as it does for ผลเชื้อ. In the WebView the list is
  // given the viewport's height and scrolls itself. Pond headers are NOT sticky —
  // the customer asked for them to scroll away with their block.
  list: (isMobile: boolean): CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7em',
    fontSize: isMobile ? MOBILE_FONT_SIZE : DESKTOP_FONT_SIZE,
    color: grey[800],
    ...(isMobile
      ? {
          height: '100%',
          overflowY: 'auto',
          overscrollBehavior: 'none',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-x pan-y',
          userSelect: 'none',
          WebkitTouchCallout: 'none',
        }
      : {}),
  }),
  block: (isMobile: boolean): CSSProperties => ({
    flex: '0 0 auto',
    backgroundColor: grey[0],
    border: hairline(isMobile, grey[500_24]),
  }),
  pondHeader: (isMobile: boolean): CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.7em',
    minHeight: HEADER_HEIGHT,
    padding: '0.35em 0.9em',
    backgroundColor: grey[200],
    borderBottom: hairline(isMobile, grey[500_16]),
    cursor: 'pointer',
    userSelect: 'none',
  }),
  pondName: {
    fontSize: '1.2em',
    fontWeight: 700,
    lineHeight: 1.2,
  } as CSSProperties,
  toggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.3em',
    marginLeft: 'auto',
    padding: '0.4em 0.2em',
    fontSize: '1.1em',
    fontWeight: 600,
    color: primary.main,
  } as CSSProperties,
  // iOS WKWebView lets a rubber-band bounce settle outside the scroll range, which
  // detaches the pinned column from its rows, so the bounce is ruled out — on the X
  // axis ONLY. `overscrollBehavior: 'none'` also stops scroll chaining, and this strip
  // covers most of each pond block: a vertical swipe on it then never reached the
  // pond list, and on Android the page scrolled only from the pond-name header.
  scroller: (isMobile: boolean): CSSProperties => ({
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-x pan-y',
    ...(isMobile ? { overscrollBehaviorX: 'none' } : {}),
  }),
  grid: {
    width: 'max-content',
    minWidth: '100%',
  } as CSSProperties,
  line: {
    display: 'flex',
  } as CSSProperties,
  // Pinned with sticky rather than laid out as a separate column, so a วิตามิน row
  // that grows keeps its label the same height as its cells.
  labelCell: (isMobile: boolean, background: string, isTextRow = false): CSSProperties => ({
    position: 'sticky',
    left: 0,
    zIndex: 2,
    boxSizing: 'border-box',
    flex: `0 0 ${LABEL_COL_WIDTH}`,
    width: LABEL_COL_WIDTH,
    display: 'flex',
    alignItems: 'center',
    gap: '0.2em',
    padding: '0 0.7em',
    fontWeight: 600,
    // Opaque: the date columns scroll underneath it.
    background,
    borderRight: hairline(isMobile, grey[500_32]),
    borderBottom: hairline(isMobile, grey[500_16]),
    ...(isTextRow ? { minHeight: TEXT_ROW_MIN_HEIGHT } : { height: ROW_HEIGHT }),
  }),
  labelHeader: (isMobile: boolean): CSSProperties => ({
    ...tableStyles.labelCell(isMobile, grey[0]),
    height: HEADER_HEIGHT,
    fontWeight: 700,
    color: grey[600],
  }),
  // Header and label text run a touch smaller than the cells. The size goes on this
  // inner span, never on the column boxes: their widths are in em, so a smaller
  // font there would narrow the column and pull it out of line with the cells.
  smallText: {
    fontSize: '0.95em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as CSSProperties,
  unit: {
    fontSize: '0.87em',
    fontWeight: 400,
    color: grey[500],
  } as CSSProperties,
  dateHeader: (isMobile: boolean): CSSProperties => ({
    boxSizing: 'border-box',
    flex: `0 0 ${DATE_COL_WIDTH}`,
    width: DATE_COL_WIDTH,
    height: HEADER_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    color: grey[700],
    borderRight: hairline(isMobile, grey[500_16]),
    borderBottom: hairline(isMobile, grey[500_16]),
  }),
  cell: (isMobile: boolean, isTextRow: boolean, isHighlighted: boolean): CSSProperties => ({
    boxSizing: 'border-box',
    flex: `0 0 ${DATE_COL_WIDTH}`,
    width: DATE_COL_WIDTH,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontWeight: 600,
    color: isHighlighted ? primary.dark : grey[800],
    backgroundColor: isHighlighted ? HIGHLIGHT_BACKGROUND : undefined,
    borderRight: hairline(isMobile, grey[500_16]),
    borderBottom: hairline(isMobile, grey[500_16]),
    ...(isTextRow
      ? {
          minHeight: TEXT_ROW_MIN_HEIGHT,
          padding: '0.25em 0.5em',
          lineHeight: TEXT_ROW_LINE_HEIGHT,
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
        }
      : { height: ROW_HEIGHT, padding: '0 0.5em', whiteSpace: 'nowrap' }),
  }),
  // Sits between the pinned column and the oldest date while older pages remain.
  loadMoreCell: (isMobile: boolean): CSSProperties => ({
    boxSizing: 'border-box',
    flex: '0 0 3.4em',
    width: '3.4em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRight: hairline(isMobile, grey[500_16]),
  }),
}
